import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { BOXES } from '../../config/infra';
import { fetchBoxVolumes, type VolumesStatus } from '../../api/volumesApi';
import DriveLayoutCard from './DriveLayoutCard';

// Live drive-layout section (HOME-209) — replaces the hand-maintained
// drives/{leia,luke}-raid.html static docs with the boxes' actual, current
// LVM/mdadm state, fetched the same way as the docker-status cards above:
// straight from the browser to each box's own mTLS-gated endpoint.

const REFRESH_MS = 30000;

type VolumesMap = Record<string, VolumesStatus | undefined>;

const DriveLayoutSection: React.FC = () => {
    const [volumes, setVolumes] = useState<VolumesMap>({});
    const mounted = useRef(true);

    const refresh = useCallback(async () => {
        const results = await Promise.all(
            BOXES.map(async b => [b.id, await fetchBoxVolumes(b.volumesUrl)] as const),
        );
        if (!mounted.current) return;
        setVolumes(Object.fromEntries(results));
    }, []);

    useEffect(() => {
        mounted.current = true;
        void refresh();
        const id = setInterval(() => { void refresh(); }, REFRESH_MS);
        return () => { mounted.current = false; clearInterval(id); };
    }, [refresh]);

    return (
        <Paper elevation={3} sx={{ p: 2, backgroundColor: '#1f2329' }} data-testid='drive-layout-section'>
            <Typography variant='h6' sx={{ mb: 0.5 }}>Live drive layout</Typography>
            <Typography variant='body2' sx={{ color: 'rgba(255,255,255,0.6)', mb: 2 }}>
                Actual mdadm/LVM state, read straight off each box (HOME-209) — not a static
                diagram. Auto-refreshes every {REFRESH_MS / 1000}s.
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                    gap: 2,
                    alignItems: 'stretch',
                }}
            >
                {BOXES.map(b => (
                    <DriveLayoutCard key={b.id} box={b} status={volumes[b.id]} />
                ))}
            </Box>
        </Paper>
    );
};

export default DriveLayoutSection;
